import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateCardSx,
  harmonateSectionTitleSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
  harmonateDrawerAddIconSx,
  harmonateGridActionIconSx,
  harmonateUploadOutlinedSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const STEP_TYPE_LIST_CODE = 'WORKFORCE_SCHEDULING_STEP_TYPE';
const DEFAULT_ALLOWED_ACTIONS = 'Approve,Reject,Return';

const stepTypeValue = (item) => item?.value || item?.label || item?.code || '';

const resolveDefaultStepType = (options) => {
  if (!Array.isArray(options) || options.length === 0) return '';
  const approval = options.find((item) => String(item?.code || '').toUpperCase() === 'APPROVAL');
  return stepTypeValue(approval || options[0]);
};

const createEmptyStepForm = (stepTypeOptions) => ({
  workflowStepId: null,
  stepOrder: 1,
  stepCode: '',
  stepName: '',
  workforceStepType: resolveDefaultStepType(stepTypeOptions),
  assignedRoleId: '',
  allowedActions: DEFAULT_ALLOWED_ACTIONS,
  enabled: true,
  maxHoursApprovalStep: false,
});

const WorkforceSchedulingSettings = () => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.WORKFORCE_OPTIMIZATION);
  const tenantId = localStorage.getItem('tenant_id') || '';
  const token = localStorage.getItem('token') || '';
  const base = () => process.env.REACT_APP_BACKEND_URL || '';

  const { toastState, showSuccess, showError, hideToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenantRoles, setTenantRoles] = useState([]);
  const [stepTypeOptions, setStepTypeOptions] = useState([]);
  const [workflow, setWorkflow] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [stepForm, setStepForm] = useState(createEmptyStepForm([]));

  const headers = useMemo(
    () => ({
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    }),
    [token],
  );

  const loadTenantRoles = useCallback(async () => {
    if (!tenantId) return [];
    const rolesResp = await fetch(`${base()}conductor/roles/tenant/tenant/${encodeURIComponent(tenantId)}`, {
      headers,
    });
    const masterResp = await fetch(`${base()}conductor/roles`, { headers });
    if (!rolesResp.ok || !masterResp.ok) return [];
    const tenantRolesData = await rolesResp.json();
    const masterRoles = await masterResp.json();
    const masterById = new Map((Array.isArray(masterRoles) ? masterRoles : []).map((r) => [r.id, r]));
    return (Array.isArray(tenantRolesData) ? tenantRolesData : []).map((tr) => ({
      roleId: tr.roleId,
      roleName: tr.roleName || tr.role_name || masterById.get(tr.roleId)?.roleName || masterById.get(tr.roleId)?.role_name || 'Unknown',
    }));
  }, [tenantId, headers]);

  const loadWorkflow = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [roles, stepTypesResp, workflowResp] = await Promise.all([
        loadTenantRoles(),
        fetch(`${base()}conductor/lookup-list?list_code=${encodeURIComponent(STEP_TYPE_LIST_CODE)}&tenant_id=${encodeURIComponent(tenantId)}`, { headers }),
        fetch(`${base()}conductor/workforce-scheduling/workflow?tenant_id=${encodeURIComponent(tenantId)}`, {
          headers,
        }),
      ]);
      setTenantRoles(roles);
      // Step types are optional until workforce scheduling is seeded for the tenant.
      if (stepTypesResp.ok) {
        const stepTypesData = await stepTypesResp.json();
        const options = Array.isArray(stepTypesData?.list_items) ? stepTypesData.list_items : [];
        setStepTypeOptions(options);
      } else {
        setStepTypeOptions([]);
      }
      if (!workflowResp.ok) {
        throw new Error(`Failed to load workflow (${workflowResp.status})`);
      }
      const data = await workflowResp.json();
      setWorkflow(data);
    } catch (error) {
      showError(error.message || 'Failed to load scheduling workflow');
    } finally {
      setLoading(false);
    }
  }, [tenantId, headers, loadTenantRoles, showError]);

  useEffect(() => {
    loadWorkflow();
  }, [loadWorkflow]);

  const steps = useMemo(() => {
    const raw = workflow?.steps || workflow?.Steps || [];
    return [...(Array.isArray(raw) ? raw : [])].sort(
      (a, b) => (a.stepOrder || a.step_order || 0) - (b.stepOrder || b.step_order || 0),
    );
  }, [workflow]);

  const flowLabel = useMemo(() => {
    if (!steps.length) return 'No steps configured';
    return steps
      .filter((s) => s.enabled !== false)
      .map((s) => s.stepName || s.step_name || 'Step')
      .join(' → ');
  }, [steps]);

  const openAddDialog = () => {
    if (!canAdd) return;
    const nextOrder = steps.length
      ? Math.max(...steps.map((s) => s.stepOrder || s.step_order || 0)) + 1
      : 1;
    setStepForm({
      ...createEmptyStepForm(stepTypeOptions),
      stepOrder: nextOrder,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (step) => {
    if (!canEdit) return;
    setStepForm({
      workflowStepId: step.workflowStepId || step.workflow_step_id || null,
      stepOrder: step.stepOrder || step.step_order || 1,
      stepCode: step.stepCode || step.step_code || '',
      stepName: step.stepName || step.step_name || '',
      workforceStepType: step.workforceStepType || step.workforce_step_type || resolveDefaultStepType(stepTypeOptions),
      assignedRoleId: step.assignedRoleId || step.assigned_role_id || '',
      allowedActions: step.allowedActions || step.allowed_actions || DEFAULT_ALLOWED_ACTIONS,
      enabled: step.enabled !== false,
      maxHoursApprovalStep: Boolean(step.maxHoursApprovalStep || step.max_hours_approval_step),
    });
    setDialogOpen(true);
  };

  const handleSaveStep = async () => {
    if (!tenantId) {
      showError('Tenant not found. Log in again.');
      return;
    }
    if (!stepForm.stepName?.trim()) {
      showError('Step name is required.');
      return;
    }
    if (!stepForm.workforceStepType) {
      showError('Select a step type.');
      return;
    }
    if (!stepForm.assignedRoleId) {
      showError('Select an assigned role (group).');
      return;
    }
    try {
      setSaving(true);
      const isEdit = Boolean(stepForm.workflowStepId);
      const payload = {
        tenantId,
        workflowStepId: stepForm.workflowStepId,
        stepOrder: Number(stepForm.stepOrder) || 1,
        stepCode: stepForm.stepCode?.trim() || null,
        stepName: stepForm.stepName.trim(),
        workforceStepType: stepForm.workforceStepType,
        assignedRoleId: stepForm.assignedRoleId,
        allowedActions: stepForm.allowedActions?.trim() || DEFAULT_ALLOWED_ACTIONS,
        enabled: stepForm.enabled,
        maxHoursApprovalStep: stepForm.maxHoursApprovalStep,
      };
      const resp = await fetch(`${base()}conductor/workforce-scheduling/workflow/steps`, {
        method: isEdit ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `Save failed (${resp.status})`);
      }
      setDialogOpen(false);
      showSuccess(isEdit ? 'Workflow step updated.' : 'Workflow step added.');
      await loadWorkflow();
    } catch (error) {
      showError(error.message || 'Failed to save workflow step');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStep = async (step) => {
    if (!canDelete) return;
    const stepId = step.workflowStepId || step.workflow_step_id;
    if (!stepId) return;
    if (!window.confirm(`Delete step "${step.stepName || step.step_name}"?`)) return;
    try {
      setSaving(true);
      const resp = await fetch(
        `${base()}conductor/workforce-scheduling/workflow/steps/${stepId}?tenant_id=${encodeURIComponent(tenantId)}`,
        { method: 'DELETE', headers },
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `Delete failed (${resp.status})`);
      }
      showSuccess('Workflow step deleted.');
      await loadWorkflow();
    } catch (error) {
      showError(error.message || 'Failed to delete workflow step');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={0} sx={{ ...harmonateCardSx, p: 3, bgcolor: ht.white }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ ...harmonateSectionTitleSx, mb: 0.5 }}>
              {workflow?.workflowName || workflow?.workflow_name || 'Workforce Scheduling'}
            </Typography>
            <Typography variant="body2" sx={{ color: ht.text, mb: 1 }}>
              {workflow?.workflowDescription
                || workflow?.workflow_description
                || 'Define multiple workflow steps and assign a role (group) to each step.'}
            </Typography>
            <Typography variant="caption" sx={{ color: ht.fadedText }}>
              Flow: {flowLabel}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Add workflow step">
              <IconButton onClick={openAddDialog} disabled={saving || !canAdd} sx={harmonateDrawerAddIconSx}>
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Button variant="outlined" size="small" onClick={loadWorkflow} disabled={saving} sx={harmonateUploadOutlinedSx}>
              Refresh
            </Button>
          </Box>
        </Box>

        {tenantRoles.length === 0 ? (
          <Typography variant="body2" sx={{ color: ht.red, mb: 2 }}>
            No roles on this tenant. Add roles under Tenant Role first (e.g. GENERAL MANAGER).
          </Typography>
        ) : null}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={70} sx={{ color: ht.text, fontWeight: 700 }}>Order</TableCell>
                <TableCell sx={{ color: ht.text, fontWeight: 700 }}>Step Name</TableCell>
                <TableCell width={120} sx={{ color: ht.text, fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ color: ht.text, fontWeight: 700 }}>Assigned Role</TableCell>
                <TableCell sx={{ color: ht.text, fontWeight: 700 }}>Allowed Actions</TableCell>
                <TableCell width={90} sx={{ color: ht.text, fontWeight: 700 }}>Status</TableCell>
                <TableCell width={100} align="right" sx={{ color: ht.text, fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {steps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" sx={{ color: ht.text }}>
                      No workflow steps yet. Click + to add your first step.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                steps.map((step) => {
                  const isMaxHours = Boolean(step.maxHoursApprovalStep || step.max_hours_approval_step);
                  return (
                    <TableRow key={step.workflowStepId || step.workflow_step_id} hover>
                      <TableCell>{step.stepOrder || step.step_order}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          {step.stepName || step.step_name}
                          {isMaxHours ? (
                            <Chip label="Max hours override" size="small" color="primary" variant="outlined" />
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell>{step.workforceStepType || step.workforce_step_type || '—'}</TableCell>
                      <TableCell>{step.assignedRoleName || step.assigned_role_name || '—'}</TableCell>
                      <TableCell>{step.allowedActions || step.allowed_actions || 'Approve'}</TableCell>
                      <TableCell>
                        <Chip
                          label={step.enabled !== false ? 'Active' : 'Inactive'}
                          size="small"
                          color={step.enabled !== false ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEditDialog(step)} disabled={saving || !canEdit} sx={harmonateGridActionIconSx}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteStep(step)} disabled={saving || !canDelete} sx={harmonateGridActionIconSx}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="body2" sx={{ color: ht.text, mt: 2 }}>
          Mark one step as <strong>Max hours override</strong> so users with that step&apos;s role can approve
          schedule assignments over weekly max hours in Assign. Assign roles under <strong>Users → Roles</strong>.
        </Typography>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>{stepForm.workflowStepId ? 'Edit Workflow Step' : 'Add Workflow Step'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'visible' }}>
          <TextField
            label="Step sequence"
            type="number"
            size="small"
            required
            fullWidth
            inputProps={{ min: 1 }}
            value={stepForm.stepOrder}
            onChange={(e) => setStepForm((prev) => ({ ...prev, stepOrder: e.target.value }))}
          />
          <TextField
            label="Step code"
            size="small"
            fullWidth
            value={stepForm.stepCode}
            onChange={(e) => setStepForm((prev) => ({ ...prev, stepCode: e.target.value }))}
            helperText="Optional stable code (e.g. MAX_HOURS_OVERRIDE)"
          />
          <TextField
            label="Step name"
            size="small"
            required
            fullWidth
            value={stepForm.stepName}
            onChange={(e) => setStepForm((prev) => ({ ...prev, stepName: e.target.value }))}
          />
          <TextField
            select
            label="Step type"
            size="small"
            required
            fullWidth
            value={stepForm.workforceStepType}
            onChange={(e) => setStepForm((prev) => ({ ...prev, workforceStepType: e.target.value }))}
            disabled={!stepTypeOptions.length}
            SelectProps={{ MenuProps: harmonateSelectMenuProps }}
          >
            {stepTypeOptions.map((item) => {
              const value = stepTypeValue(item);
              return (
                <MenuItem key={item.code || value} value={value}>
                  {item.label || value}
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            select
            label="Assigned role (group)"
            size="small"
            required
            fullWidth
            value={stepForm.assignedRoleId}
            onChange={(e) => setStepForm((prev) => ({ ...prev, assignedRoleId: e.target.value }))}
            SelectProps={{ MenuProps: harmonateSelectMenuProps }}
          >
            <MenuItem value="">
              <em>Select role</em>
            </MenuItem>
            {tenantRoles.map((role) => (
              <MenuItem key={role.roleId} value={role.roleId}>
                {role.roleName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Allowed actions"
            size="small"
            fullWidth
            value={stepForm.allowedActions}
            onChange={(e) => setStepForm((prev) => ({ ...prev, allowedActions: e.target.value }))}
            helperText="Any comma-separated labels (e.g. Approve, Reject, Return). Stored as configured."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={stepForm.maxHoursApprovalStep}
                onChange={(e) => setStepForm((prev) => ({ ...prev, maxHoursApprovalStep: e.target.checked }))}
              />
            }
            label="This step handles max-hours override approval in Schedule Assign"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={stepForm.enabled}
                onChange={(e) => setStepForm((prev) => ({ ...prev, enabled: e.target.checked }))}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveStep} disabled={saving} sx={harmonateProceedButtonSx}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Toast open={toastState.open} message={toastState.message} severity={toastState.severity} onClose={hideToast} />
    </Box>
  );
};

export default WorkforceSchedulingSettings;
